// ===== Enums =====

import {
    PAGE_ROUTE,
    READER_PARAMS_TYPE,
} from '../enums';

// ===== Constants =====

import {
    VERASCOPE_WEBSITE_URL,
} from '../constants/generalConstants';

// ===== Interfaces =====

import {
    IInternalPostLinkTarget,
} from '../interfaces';

const INTERNAL_POST_LINK_PARAGRAPH_ID_PARAM = 'paragraphId';
const INTERNAL_POST_LINK_PATHNAME = `/${PAGE_ROUTE.book}`;
const HAS_EXPLICIT_SCHEME_REGEX = /^[a-zA-Z][a-zA-Z\d+\-.]*:/;

/**
 * Resolves a stored editor href into a same-app internal post link target when
 * it points at the reader route and carries a paragraph anchor.
 */
const resolveInternalPostLinkTarget = (
    href: string,
    {
        defaultOrigin = VERASCOPE_WEBSITE_URL,
    }: {
        defaultOrigin?: string,
    } = {},
): IInternalPostLinkTarget | null => {
    const normalizedHref = `${href || ''}`.trim();
    if (!normalizedHref) {
        return null;
    }

    let parsedURL: URL;
    let parsedDefaultOrigin: URL;
    try {
        parsedDefaultOrigin = new URL(defaultOrigin);
        parsedURL = new URL(normalizedHref, parsedDefaultOrigin.toString());
    } catch (_error) {
        return null;
    }

    const normalizedPathname = parsedURL.pathname.replace(/\/+$/g, '') || '/';
    if (normalizedPathname !== INTERNAL_POST_LINK_PATHNAME) {
        return null;
    }

    const hrefIsRelative = !HAS_EXPLICIT_SCHEME_REGEX.test(normalizedHref)
        && !normalizedHref.startsWith('//');
    if (
        !hrefIsRelative
        && parsedURL.origin !== parsedDefaultOrigin.origin
    ) {
        return null;
    }

    const paragraphId = `${parsedURL.searchParams.get(INTERNAL_POST_LINK_PARAGRAPH_ID_PARAM) || ''}`.trim();
    if (!paragraphId) {
        return null;
    }

    const chapterId = `${parsedURL.searchParams.get(READER_PARAMS_TYPE.chapterId.toString()) || ''}`.trim();
    const sectionId = `${parsedURL.searchParams.get(READER_PARAMS_TYPE.sectionId.toString()) || ''}`.trim();

    return {
        href: normalizedHref,
        paragraphId,
        ...(chapterId ? { chapterId } : {}),
        ...(sectionId ? { sectionId } : {}),
    };
};

export default resolveInternalPostLinkTarget;
